import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Check, Copy, ExternalLink } from "lucide-react";
import { MarketingLayout, PageHero, useHydratedTranslation } from "@/components/marketing/Layout";
import { buildSeo } from "@/lib/seo";

const CHATGPT_SETTINGS_URL = "https://chatgpt.com/#settings/Connectors/Advanced";
const CLAUDE_SETTINGS_URL = "https://claude.ai/customize/connectors?modal=add-custom-connector";

export const Route = createFileRoute("/connect")({
  head: () => {
    const seo = buildSeo({
      path: "/connect",
      title: "Connect Reality Twin to your AI assistant",
      description:
        "Connect ChatGPT or Claude to Reality Twin in under a minute. Copy the MCP server URL and follow the steps.",
    });
    return { meta: seo.meta, links: seo.links };
  },
  component: ConnectPage,
});

function ConnectPage() {
  const t = useHydratedTranslation();
  const [mcpUrl, setMcpUrl] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setMcpUrl(new URL("/mcp", window.location.origin).toString());
  }, []);

  async function copy() {
    if (!mcpUrl) return;
    try {
      await navigator.clipboard.writeText(mcpUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* clipboard unavailable */
    }
  }

  const chatgptSteps = t("routes.connect.chatgptSteps", {
    returnObjects: true,
  }) as unknown as string[];
  const claudeSteps = t("routes.connect.claudeSteps", {
    returnObjects: true,
  }) as unknown as string[];

  return (
    <MarketingLayout>
      <PageHero
        eyebrow={t("routes.connect.eyebrow")}
        title={t("routes.connect.title")}
        sub={t("routes.connect.sub")}
      />

      <section className="mx-auto max-w-3xl px-6 py-16">
        <div className="rounded-2xl border border-border p-6 bg-cream/40">
          <div className="font-mono-eyebrow">{t("routes.connect.urlLabel")}</div>
          <div className="mt-3 flex items-center gap-2">
            <code className="flex-1 truncate rounded-lg bg-background border border-border px-4 py-3 text-[14px] font-mono">
              {mcpUrl || "…"}
            </code>
            <button
              type="button"
              onClick={copy}
              disabled={!mcpUrl}
              className="inline-flex items-center gap-1.5 rounded-full bg-foreground text-background px-4 py-2.5 text-[13px] font-medium hover:opacity-90 transition disabled:opacity-50"
            >
              {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
              {copied ? t("routes.connect.copied") : t("routes.connect.copy")}
            </button>
          </div>
          <p className="mt-3 text-[13px] text-muted-foreground">{t("routes.connect.note")}</p>
        </div>

        <div className="mt-12 grid gap-8 md:grid-cols-2">
          <Guide
            name="ChatGPT"
            steps={chatgptSteps}
            settingsHref={CHATGPT_SETTINGS_URL}
            settingsLabel={t("routes.connect.openLink")}
          />
          <Guide
            name="Claude"
            steps={claudeSteps}
            settingsHref={CLAUDE_SETTINGS_URL}
            settingsLabel={t("routes.connect.openLink")}
          />
        </div>
      </section>
    </MarketingLayout>
  );
}

function Guide({
  name,
  steps,
  settingsHref,
  settingsLabel,
}: {
  name: string;
  steps: string[];
  settingsHref: string;
  settingsLabel: string;
}) {
  return (
    <div className="rounded-2xl border border-border p-6">
      <div className="flex items-center justify-between">
        <div className="font-mono-eyebrow">{name}</div>
        <a
          href={settingsHref}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-[12px] text-muted-foreground underline hover:text-foreground transition"
        >
          {settingsLabel}
          <ExternalLink className="h-3 w-3" />
        </a>
      </div>
      <ol className="mt-4 space-y-4 text-[14.5px] leading-[1.6]">
        {steps.map((s, i) => (
          <li key={i} className="flex gap-3">
            <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-foreground text-background text-[11px] font-medium">
              {i + 1}
            </span>
            <span className="text-muted-foreground">{s}</span>
          </li>
        ))}
      </ol>
    </div>
  );
}
